import type { QueryClient } from '@tanstack/react-query';
import type { SqlImportsImportReply, SqlImportsListResponse } from '@customerio/hydra-ui-api';
import { setSqlImportDatabaseCache } from '../sql-import-database/cache';
import { parseSqlImportDatabase, type SqlImportDatabase } from '../sql-import-database/entity';
import { setSqlImportCache } from './cache';
import type { SqlImport, SqlImportWithDatabase } from './entity';

/**
 * Normalize a single-import reply — the shape of get, create, update, start,
 * stop and update_query — so the draft placeholder database reads as unnamed
 * wherever the reply travels, not only in the cache.
 */
export function parseSqlImportReply(raw: SqlImportsImportReply): SqlImportsImportReply {
  return { sql_import: raw.sql_import, sql_import_database: parseSqlImportDatabase(raw.sql_import_database) };
}

/** Write a parsed single-import reply into the cache: the import into its detail slot, the database into its own. */
export function populateSqlImportReply(qc: QueryClient, workspaceId: number, reply: SqlImportsImportReply): SqlImport {
  setSqlImportCache(qc, workspaceId, reply.sql_import);
  setSqlImportDatabaseCache(qc, workspaceId, reply.sql_import_database);
  return reply.sql_import;
}

/**
 * Write a list reply into the cache and return the imports joined to their
 * databases. Each import lands in its detail slot and each database in its
 * own, so a follow-up detail read resolves without a request.
 */
export function populateSqlImportList(
  qc: QueryClient,
  workspaceId: number,
  list: SqlImportsListResponse,
): SqlImportWithDatabase[] {
  const databasesById = new Map<string, SqlImportDatabase>();
  // The spec types both fields as arrays, and services sends `[]` for an empty list since the
  // fix in services#25837. A build older than that sends `null` instead, which would take the
  // whole page down for a workspace with no syncs — the first thing a new customer sees. Read
  // them as possibly absent so the page renders empty rather than crashing.
  for (const raw of list.sql_import_databases ?? []) {
    const database = parseSqlImportDatabase(raw);
    setSqlImportDatabaseCache(qc, workspaceId, database);
    databasesById.set(database.id, database);
  }

  return (list.sql_imports ?? []).map((sqlImport) => {
    setSqlImportCache(qc, workspaceId, sqlImport);
    return { ...sqlImport, database: databasesById.get(sqlImport.database_id) ?? null };
  });
}
